import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import MovieCard from '../components/MovieCard';
import { movieApi, WatchlistItem, handleApiError } from '../services/api';

const Watchlist: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [filteredWatchlist, setFilteredWatchlist] = useState<WatchlistItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unwatched' | 'watched'>('all');
  const [sortBy, setSortBy] = useState<'added' | 'title' | 'year'>('added');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchWatchlist();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    applyFiltersAndSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlist, filter, sortBy]);

  const fetchWatchlist = async () => {
    try {
      setIsLoading(true);
      const response = await movieApi.getWatchlist();
      setWatchlist(response.watchlist);
    } catch (error) {
      setError(handleApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...watchlist];

    // Apply filter
    if (filter === 'watched') {
      filtered = filtered.filter(item => item.watchlist_watched);
    } else if (filter === 'unwatched') {
      filtered = filtered.filter(item => !item.watchlist_watched);
    }

    // Apply sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return (a.omdb_title || a.cleaned_title).localeCompare(b.omdb_title || b.cleaned_title);
        case 'year':
          return (b.year || 0) - (a.year || 0);
        case 'added':
        default:
          return new Date(b.watchlist_added_at).getTime() - new Date(a.watchlist_added_at).getTime();
      }
    });

    setFilteredWatchlist(filtered);
  };

  const handleRemoveFromWatchlist = async (movieId: number) => {
    try {
      await movieApi.removeFromWatchlist(movieId);
      setWatchlist(prev => prev.filter(item => item.id !== movieId));
    } catch (error) {
      console.error('Failed to remove from watchlist:', handleApiError(error));
    }
  };

  const handleToggleWatched = async (movieId: number, watched: boolean) => {
    try {
      await movieApi.markAsWatched(movieId, watched);
      setWatchlist(prev => prev.map(item =>
        item.id === movieId
          ? {
              ...item,
              watchlist_watched: watched,
              watchlist_watched_at: watched ? new Date().toISOString() : undefined
            }
          : item
      ));
    } catch (error) {
      console.error('Failed to update watched status:', handleApiError(error));
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h1>
            <p className="text-gray-600">Please sign in to view your watchlist.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your watchlist...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchWatchlist}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const watchedCount = watchlist.filter(item => item.watchlist_watched).length;
  const unwatchedCount = watchlist.length - watchedCount;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Watchlist</h1>
            <p className="text-gray-600">
              {watchlist.length} movies total • {unwatchedCount} unwatched • {watchedCount} watched
            </p>
          </div>

          {watchlist.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Your watchlist is empty</h3>
              <p className="text-gray-600 mb-6">Start adding movies you want to watch!</p>
              <a
                href="/browse"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Browse Movies
              </a>
            </div>
          ) : (
            <>
              {/* Controls */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  {/* Filter Controls */}
                  <div className="flex items-center space-x-4">
                    <label className="text-sm font-medium text-gray-700">Filter:</label>
                    <select
                      value={filter}
                      onChange={(e) => setFilter(e.target.value as 'all' | 'unwatched' | 'watched')}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Movies ({watchlist.length})</option>
                      <option value="unwatched">Unwatched ({unwatchedCount})</option>
                      <option value="watched">Watched ({watchedCount})</option>
                    </select>
                  </div>

                  {/* Sort Controls */}
                  <div className="flex items-center space-x-4">
                    <label className="text-sm font-medium text-gray-700">Sort by:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as 'added' | 'title' | 'year')}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="added">Date Added</option>
                      <option value="title">Title</option>
                      <option value="year">Year</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Movie Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredWatchlist.map((movie) => (
                  <div key={movie.id} className="relative">
                    <MovieCard
                      movie={movie}
                      onClick={() => window.location.href = `/movie/${movie.id}`}
                      showSimilarity={false}
                      showStreaming={false}
                      size="medium"
                    />

                    {/* Watchlist Controls */}
                    <div className="absolute top-2 right-2 flex flex-col gap-2">
                      {/* Remove from watchlist */}
                      <button
                        onClick={() => handleRemoveFromWatchlist(movie.id)}
                        className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors shadow-lg"
                        title="Remove from watchlist"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>

                      {/* Toggle watched status */}
                      <button
                        onClick={() => handleToggleWatched(movie.id, !movie.watchlist_watched)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors shadow-lg ${
                          movie.watchlist_watched
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                        title={movie.watchlist_watched ? 'Mark as unwatched' : 'Mark as watched'}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                    </div>

                    {/* Watched Badge */}
                    {movie.watchlist_watched && (
                      <div className="absolute bottom-2 left-2">
                        <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">
                          Watched
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {filteredWatchlist.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-600">No movies match the current filter.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Watchlist;